// Live's Session View, as rows a player can draw.
//
// A Set captured with a Session View carries its clips in SLOTS, not on the
// timeline: `Lane.sessionClips`, each with the scene row it sits in, and the
// snapshot's scene names beside them. This turns that into rows × lanes —
// every row carrying one cell per lane, empty where the lane has nothing, so
// a cell is always under its own track header.
//
// Not to be confused with `components/SessionGrid.tsx`, which renders the same
// data for a pointer — drag between slots, rename scenes, record. This is the
// plain shape both that and the player's grid could be drawn from.
//
// Pure data: the component decides how a cell looks, this decides where it is.

import { launchableSlotClip } from "@web-shared/lib/session-launch";
import type { Arrangement, Lane, LaneClip } from "@web-shared/lib/arrangement";

export interface SceneRow {
  /** Live's scene name, or the row number where the Set never named it. */
  label: string;
  /** 0-based scene index — what a launch acts on. */
  row: number;
  /** One entry per lane, in `lanes` order. Null where the lane's slot is empty. */
  cells: Array<LaneClip | null>;
  /** A row holding only inferred clips (lib/ghost-clips.ts) — never one Live
   * has. Rows are all-ghost or all-real: ghosts start after the last scene. */
  ghost: boolean;
}

export interface SceneGrid {
  lanes: Lane[];
  rows: SceneRow[];
  /** Slot clips across every lane. */
  clipCount: number;
}

export function buildSceneGrid(arrangement: Arrangement): SceneGrid {
  const lanes = arrangement.lanes;
  const bySlot = lanes.map((lane) => {
    const map = new Map<number, LaneClip>();
    for (const clip of lane.sessionClips) {
      const slot = clip.slot ?? 0;
      // Live allows one clip per slot; if a snapshot ever carried two, the
      // first wins rather than the later one silently replacing it.
      if (!map.has(slot)) map.set(slot, clip);
    }
    return map;
  });

  const rows: SceneRow[] = Array.from({ length: arrangement.sceneCount }, (_, row) => {
    const cells = bySlot.map((map) => map.get(row) ?? null);
    return {
      label: arrangement.sceneNames[row]?.trim() || String(row + 1),
      row,
      cells,
      // An empty row is not a ghost row — there is nothing in it to infer.
      ghost: cells.some(Boolean) && cells.every((c) => !c || c.ghost === true),
    };
  });

  return {
    lanes,
    rows,
    clipCount: rows.reduce((n, r) => n + r.cells.filter(Boolean).length, 0),
  };
}

/**
 * The grid in two bands: the timeline's material, then the Set's scenes.
 *
 * `buildSceneGrid` numbers rows by slot, which is what a launch acts on and
 * must never be reordered. What a SCREEN puts first is a different question,
 * and for the room the answer is the material: a pad is a thing you hit, and
 * most Sets that reach the room have all of theirs on the timeline and few or
 * no scenes. The Set's own Session View follows it, keeping Live's numbers and
 * names — `row.row` is untouched either way, so `sceneLaunchPlan` cannot tell
 * the difference.
 *
 * Every row is all-ghost, all-real or empty (`SceneRow.ghost`), so this is a
 * clean partition; an empty declared scene is the Set's, and sorts with the
 * scenes.
 */
export function splitBands(grid: SceneGrid): { material: SceneRow[]; scenes: SceneRow[] } {
  const material: SceneRow[] = [];
  const scenes: SceneRow[] = [];
  for (const row of grid.rows) (row.ghost ? material : scenes).push(row);
  return { material, scenes };
}

/**
 * What a tap on a slot cell does.
 *
 * Live's Session View gives every cell a launch button and a stop button and a
 * pointer precise enough to hit either. Touch has one gesture, so the tap
 * carries both meanings: the clip the lane is already playing stops it, any
 * other clip takes the lane. A clip with nothing to sound — an audio slot clip
 * whose object never uploaded — offers neither rather than a button that lies
 * (`launchableSlotClip` is the same rule the desktop grid and the scene plan
 * use, so one seed cannot be launchable in one view and not the other).
 */
export function slotTapAction(
  clip: LaneClip,
  lane: Lane,
  playing: Record<string, string>,
): "launch" | "stop" | "inert" {
  if (playing[lane.id] === clip.id) return "stop";
  return launchableSlotClip(clip) ? "launch" : "inert";
}
